import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
} from "@/components/ui/sheet";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, FieldPath } from "react-hook-form";
import { Button } from "@/components/ui/button";
import CompactStepIndicator from "@/components/ui/compact-step-indicator";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { ConsultationStatus } from "@/types/enums";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  useCreateConsultationMutation,
  useUpdateConsultationMutation,
  useSubmitConsultationAnswersMutation,
  useGetQuestionsQuery,
  useCreateMeasurementMutation,
  useCreateBodyAnalysisMutation,
  useUpdateBodyAnalysisMutation,
  useGetBodyAnalysisQuery,
  useCreatePostureAnalysisMutation,
  useUpdatePostureAnalysisMutation,
  useGetPostureAnalysisQuery,
  useCreateGoalMutation,
  useUpdateGoalMutation,
  useGetGoalQuery,
  useAssignMealPlanMutation,
  useAssignWorkoutPlanMutation,
  usePatchConsultationStatusMutation,
} from "@/services/consultationApi";
import { useCreateMealPlansMutation } from "@/services/mealPlansApi";
import { FormProps, ErrorType } from "@/types";
import { Consultation, FormQuestionResponse, AnswerValue, BodyAnalysis, PostureAnalysis } from "@/types/consultation";
import Background from "./steps/background";
import BodyMeasurements, { BODY_PARTS } from "./steps/body-measurements";
import BodyAnalysisStep, { sections } from "./steps/body-analysis";
import PostureAnalysisStep from "./steps/posture-analysis";
import GoalStep from "./steps/goal";
import MealPlanStep from "./steps/meal-plan";
import WorkoutPlanStep from "./steps/workout-plan";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
  setAction?: (action: "add" | "edit") => void;
  refetch?: () => void;
  openSections?: string[];
  setOpenSections?: (sections: string[]) => void;
}

// Placeholder step components - to be replaced with actual step components later
const Step1Component = (props: FormStepProps) => <Background {...props} />;
const Step2Component = (props: FormStepProps) => <BodyMeasurements {...props} />;
const Step3Component = (props: FormStepProps) => <BodyAnalysisStep {...props} />;
const Step4Component = (props: FormStepProps) => <PostureAnalysisStep {...props} />;
const Step5Component = (props: FormStepProps) => <GoalStep {...props} />;
const Step6Component = (props: FormStepProps) => <MealPlanStep {...props} />;
const Step7Component = (props: FormStepProps) => <WorkoutPlanStep {...props} />;

const stepContentComponents = [
  Step1Component,
  Step2Component,
  Step3Component,
  Step4Component,
  Step5Component,
  Step6Component,
  Step7Component,
];

const getStepContent = (step: number, formProps: FormStepProps) => {
  const Component = stepContentComponents[step - 1];
  return Component ? <Component {...formProps} /> : null;
};

const ConsultationForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  data,
  setData,
  refetch,
}: FormProps<Consultation>) => {
  const { toast } = useToast();
  const [activeStep, setActiveStep] = useState(1);
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [showWarning, setShowWarning] = useState(false);
  const [createConsultation, { isLoading: isCreating }] = useCreateConsultationMutation();
  const [updateConsultation, { isLoading: isUpdating }] = useUpdateConsultationMutation();

  // Define initial values for the form
  const initialValues: Consultation = {
    id: undefined,
    member_id: undefined,
    member_name: "",
    membership_id: undefined,
    own_member_id: "",
    member_dob: "",
    coach_id: undefined,
    coach_name: "",
    consultation_date: new Date().toISOString(),
    status: ConsultationStatus.Draft,
    notes: "",
  };

  // Define steps configuration
  const steps = useMemo(
    () => [
      { label: "Background", key: 1 },
      { label: "Body Measurements", key: 2 },
      { label: "Body Analysis", key: 3 },
      { label: "Posture Analysis", key: 4 },
      { label: "Goal", key: 5 },
      { label: "Meal Plan", key: 6 },
      { label: "Workout Plan", key: 7 },
    ],
    []
  );

  const form = useForm<Consultation>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    clearErrors,
    reset,
    trigger,
    watch,
    formState: { isSubmitting, errors, isDirty },
  } = form;
  const watcher = watch();
  console.log({ watcher, errors })
  // Reset form when dialog opens/closes or action changes
  useEffect(() => {
    if (!isOpen) return;
    if (action === "edit" && data) {
      reset(data as Consultation);
    } else if (action === "add") {
      setActiveStep(1);
      clearErrors();
      reset(initialValues);
    }
  }, [action, isOpen, data, reset, clearErrors]);

  // Ensure step index never exceeds steps length
  useEffect(() => {
    if (activeStep > steps.length) {
      setActiveStep(1);
    }
  }, [steps.length]);

  const onSubmit = async (formData: Consultation, event?: React.BaseSyntheticEvent) => {
    // Prevent form submission via Enter key on intermediate steps
    if (activeStep !== steps.length) {
      event?.preventDefault();
      return;
    }

    try {
      // Final submission logic for the last step
      // if (action === "edit") {
      //   await updateConsultation({ ...formData, org_id: orgId }).unwrap();
      //   toast({
      //     description: "Consultation updated and submitted successfully",
      //     variant: "success",
      //   });
      // }

      // If on the last step (Workout Plan), also assign workouts and update status
      if (activeStep === 7) {
        const assignedWorkoutPlanIds = formData.assigned_workout_plan_ids;
        if (assignedWorkoutPlanIds && assignedWorkoutPlanIds.length > 0) {
          await assignWorkoutPlan({
            consultation_id: formData.id!,
            org_id: orgId,
            data: { workout_plan_ids: assignedWorkoutPlanIds },
          }).unwrap();
          toast({
            description: "Workout plans assigned successfully",
            variant: "success",
          });
        }

        // Update status to awaiting_approval
        await patchConsultationStatus({
          consultation_id: formData.id!,
          org_id: orgId,
          status: ConsultationStatus.AwaitingApproval,
        }).unwrap();
        
        toast({
          description: "Consultation submitted for approval",
          variant: "success",
        });
      }

      refetch();
      forceClose();
    } catch (error: unknown) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail ||
            (typedError.data as { message?: string }).message
            }`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    if (isDirty) {
      setShowWarning(true);
    } else {
      forceClose();
    }
  };

  const forceClose = () => {
    setShowWarning(false);
    clearErrors();
    setActiveStep(1);
    setAction("add");
    setData(undefined);
    reset(initialValues);
    setOpen(false);
  };

  const [submitConsultationAnswers, { isLoading: isSubmittingAnswers }] = useSubmitConsultationAnswersMutation();
  const [createMeasurement, { isLoading: isSavingMeasurement }] = useCreateMeasurementMutation();
  const [createBodyAnalysis, { isLoading: isSavingBodyAnalysis }] = useCreateBodyAnalysisMutation();
  const [updateBodyAnalysis, { isLoading: isUpdatingBodyAnalysis }] = useUpdateBodyAnalysisMutation();
  const [createPostureAnalysis, { isLoading: isSavingPostureAnalysis }] = useCreatePostureAnalysisMutation();
  const [updatePostureAnalysis, { isLoading: isUpdatingPostureAnalysis }] = useUpdatePostureAnalysisMutation();
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  
  // Get consultation ID from form to check for existing analysis data
  const consultationId = watch("id");
  const { currentData: existingBodyAnalysis, isFetching: isFetchingBodyAnalysis } = useGetBodyAnalysisQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || activeStep !== 3 || !isOpen }
  );
  const { currentData: existingPostureAnalysis, isFetching: isFetchingPostureAnalysis } = useGetPostureAnalysisQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || activeStep !== 4 || !isOpen }
  );
  console.log({existingPostureAnalysis})
  const { currentData: existingGoal, isFetching: isFetchingGoal } = useGetGoalQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || activeStep !== 5 || !isOpen }
  );
  console.log({consultationId})

  const [createGoal, { isLoading: isCreatingGoal }] = useCreateGoalMutation();
  const [updateGoal, { isLoading: isUpdatingGoal }] = useUpdateGoalMutation();
  const [assignMealPlan, { isLoading: isAssigningMealPlan }] = useAssignMealPlanMutation();
  const [assignWorkoutPlan, { isLoading: isAssigningWorkoutPlan }] = useAssignWorkoutPlanMutation();
  const [patchConsultationStatus, { isLoading: isPatchingStatus }] = usePatchConsultationStatusMutation();
  const [createMealPlans, { isLoading: isCreatingMealPlan }] = useCreateMealPlansMutation();
  const { data: questionsData } = useGetQuestionsQuery({
    org_id: orgId,
    section_key: ["medical", "nutrition", "exercise"],
  }, { skip: activeStep !== 1 || !isOpen });

  const allQuestions = useMemo<FormQuestionResponse[]>(() => {
    if (!questionsData?.data) return [];
    return Object.values(questionsData.data).flat() as FormQuestionResponse[];
  }, [questionsData]);

  const handleNext = async () => {
    let hasError = false;

    if (activeStep === 1) {
      // Step 1: Background & Dynamic Questions
      
      // 1. Validate basic fields using trigger (they are always mounted)
      const basicFields: FieldPath<Consultation>[] = ["member_id", "coach_id", "membership_id", "consultation_date"];
      const isBasicValid = await trigger(basicFields, { shouldFocus: true });
      if (!isBasicValid) hasError = true;

      // 2. Manually check dynamic questions (handles unmounted/closed accordion content)
      const values = form.getValues();
      const sectionsWithErrors = new Set<string>();

      allQuestions.forEach((q) => {
        if (q.is_required && q.question_type !== "toggle") {
          const answer = values.answers?.[String(q.id)];
          const isEmpty = 
            answer === undefined || 
            answer === null || 
            answer === "" || 
            (Array.isArray(answer) && answer.length === 0);
          
          if (isEmpty) {
            form.setError(`answers.${q.id}` as FieldPath<Consultation>, { 
              type: "required", 
              message: "This field is required" 
            });
            hasError = true;
            sectionsWithErrors.add(q.section_key);
          }
        }
      });

      if (sectionsWithErrors.size > 0) {
        setOpenSections(Array.from(sectionsWithErrors));
      }
    } else {
      // Default validation for other steps for now
      const isStepValid = await trigger(undefined, { shouldFocus: true });
      if (!isStepValid) hasError = true;
    }

    if (hasError) {
      toast({
        variant: "destructive",
        description: "Please fill all the mandatory fields for this step",
      });
      return;
    }

    // Step 1: Save Background & Answers before proceeding
    if (activeStep === 1) {
      const values = form.getValues();
      let currentId = values.id;

      // Optimization: If already has ID and no changes made, just proceed
      if (currentId && !isDirty) {
        if (activeStep < steps.length) {
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
        return;
      }

      try {
        // 1. Create or Update Consultation
        if (!currentId) {
          // Explicitly ensure all required fields are present
          if (!values.member_id || !values.coach_id || !values.membership_id || !values.consultation_date) {
            toast({
              variant: "destructive",
              description: "Missing required consultation details. Please ensure Member, Coach, Membership, and Date are all selected.",
            });
            return;
          }

          const createPayload = {
            member_id: Number(values.member_id),
            membership_id: Number(values.membership_id),
            coach_id: Number(values.coach_id),
            consultation_date: values.consultation_date,
            status: values.status,
            org_id: orgId,
          };

          const res = await createConsultation(createPayload).unwrap();
          currentId = res.id;
          form.setValue("id", currentId);
          setAction?.("edit");
        } else {
          // Explicit validation for update payload
          if (!values.member_id || !values.coach_id || !values.membership_id || !values.consultation_date) {
            toast({
              variant: "destructive",
              description: "Missing required consultation details. Information cannot be updated.",
            });
            return;
          }

          const updatePayload = {
            member_id: Number(values.member_id),
            membership_id: Number(values.membership_id),
            coach_id: Number(values.coach_id),
            consultation_date: values.consultation_date,
            status: values.status,
            meal_plan_id: values.assigned_meal_plan_ids ?? 0,
            workout_plan_id: values.assigned_workout_plan_ids ?? 0,
          };
          await updateConsultation({ ...updatePayload, id: values.id, org_id: orgId } as any).unwrap();
        }

        // 2. Submit Answers
        const answers = values.answers || {};
        const answersToSubmit = Object.entries(answers).map(([qId, answer]) => {
          const question = allQuestions.find((q) => String(q.id) === qId);
          let finalAnswer = answer;
          
          // Ensure toggle questions default to false if not truthy
          if (question?.question_type === "toggle") {
            finalAnswer = answer === true || answer === "true" || answer === 1;
          }

          return {
            question_id: Number(qId),
            question_type: question?.question_type || "text",
            answer: finalAnswer as AnswerValue,
          };
        });

        // Add missing toggle questions as false
        allQuestions.forEach((q) => {
          if (q.question_type === "toggle" && !answers[String(q.id)]) {
            answersToSubmit.push({
              question_id: Number(q.id),
              question_type: "toggle",
              answer: false,
            });
          }
        });

        if (answersToSubmit.length > 0) {
          await submitConsultationAnswers({
            consultation_id: currentId!,
            org_id: orgId,
            answers: answersToSubmit,
          }).unwrap();
        }

        toast({
          description: values.id ? "Progress updated" : "Consultation created and progress saved",
          variant: "success",
        });
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      } catch (error: unknown) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error saving progress",
          description: typedError?.data?.message || typedError.data?.detail || "Failed to save progress",
        });
        return; // Stop here if saving failed
      }
      return;
    }

    if (activeStep === 2) {
      // Step 2: Body Measurements
      const values = form.getValues();
      const consultationId = values.id; // Ensure we have the ID from step 1
      const measurement = values.body_measurement || {};

      // Check if mandatory body parts (chest and abs) are measured
      const mandatoryParts = ["chest", "abs"];
      const missingParts: string[] = [];

      mandatoryParts.forEach((part) => {
        if (!measurement[part]) {
          missingParts.push(BODY_PARTS[part as keyof typeof BODY_PARTS]);
        }
      });

      if (missingParts.length > 0) {
        toast({
          variant: "destructive",
          title: "Incomplete Measurements",
          description: `Please add measurements for mandatory parts: ${missingParts.join(", ")}`,
        });
        return;
      }

      if (consultationId && Object.keys(measurement).length > 0) {
        try {
          await createMeasurement({
            consultation_id: consultationId,
            org_id: orgId,
            data: measurement,
          }).unwrap();
          toast({
             description: "Measurements saved successfully",
             variant: "success",
          });
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
        } catch (error: unknown) {
          const typedError = error as ErrorType;
          toast({
            variant: "destructive",
            title: "Error saving measurements",
            description: typedError?.data?.message || "Failed to save measurements",
          });
          // Optionally stop here if saving failed
          return; 
        }
      } else {
         // Proceed if no ID (shouldn't happen if flow is correct) or no measurements
         setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
      return;
    }

    if (activeStep === 3) {
      // Step 3: Body Analysis
      const values = form.getValues();
      const consultationId = values.id;
      const bodyAnalysis = values.body_analysis;

      if (consultationId && bodyAnalysis) {
        // Extract age and include it at the top level of the body_analysis object
        const age = bodyAnalysis.cardio?.age ?? 
                    bodyAnalysis.endurance?.age ?? 
                    bodyAnalysis.flexibility?.age;

        // Validate mandatory fields
        const missingFields: string[] = [];
        
        if (!age || Number.isNaN(Number(age))) {
          missingFields.push("Age");
        }
        
        sections.forEach((s) => {
          if (!bodyAnalysis[s.key]?.rank) {
            missingFields.push(`${s.title} Rank`);
          }
          if (bodyAnalysis[s.key]?.rate === undefined || Number.isNaN(Number(bodyAnalysis[s.key]?.rate))) {
            missingFields.push(`${s.title} Rate`);
          } else {
            const rateVal = Number(bodyAnalysis[s.key]?.rate);
            if (rateVal < 1 || rateVal > 100) {
              missingFields.push(`${s.title} Rate must be between 1 and 100`);
            }
          }
        });

        if (missingFields.length > 0) {
          toast({
            variant: "destructive",
            title: "Missing Required Fields",
            description: `Please fill in the following fields: ${missingFields.join(", ")}`,
          });
          return;
        }

        // Clean data: create a copy with age at top level and metrics per section
        const cleanedData: BodyAnalysis = {
          age: Number(age),
          cardio: { 
            rank: bodyAnalysis.cardio?.rank || "",
            rate: Number(bodyAnalysis.cardio?.rate || 0)
          },
          endurance: { 
            rank: bodyAnalysis.endurance?.rank || "",
            rate: Number(bodyAnalysis.endurance?.rate || 0)
          },
          flexibility: { 
            rank: bodyAnalysis.flexibility?.rank || "",
            rate: Number(bodyAnalysis.flexibility?.rate || 0)
          },
        };

        try {
          // Check if body analysis data already exists
          const hasExistingData = existingBodyAnalysis?.data && existingBodyAnalysis.data.length > 0;

          if (hasExistingData) {
            // Update existing body analysis
            await updateBodyAnalysis({
              consultation_id: consultationId,
              org_id: orgId,
              data: cleanedData,
            }).unwrap();
            toast({
              description: "Body analysis updated successfully",
              variant: "success",
            });
          } else {
            // Create new body analysis
            await createBodyAnalysis({
              consultation_id: consultationId,
              org_id: orgId,
              data: cleanedData,
            }).unwrap();
            toast({
              description: "Body analysis saved successfully",
              variant: "success",
            });
          }
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
        } catch (error: unknown) {
          const typedError = error as ErrorType;
          toast({
            variant: "destructive",
            title: "Error saving body analysis",
            description: typedError?.data?.message || "Failed to save body analysis",
          });
          return;
        }
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
      return;
    }

    if (activeStep === 4) {
      // Step 4: Posture Analysis
      const values = form.getValues();
      const consultationId = values.id;
      const postureAnalysis = values.posture_analysis;

      if (consultationId && postureAnalysis) {
        // Validate mandatory fields
        const missingFields: string[] = [];
        
        if (!postureAnalysis.overhead_squat_anterior) {
          missingFields.push("Overhead Squat – Anterior View");
        }
        
        if (!postureAnalysis.overhead_squat_lateral) {
          missingFields.push("Overhead Squat – Lateral View");
        }
        
        if (!postureAnalysis.single_leg_anterior) {
          missingFields.push("Single Leg Squat – Anterior View");
        }
        
        if (!postureAnalysis.pulling_pushing_lateral) {
          missingFields.push("Pulling & Pushing – Lateral View");
        }

        if (missingFields.length > 0) {
          toast({
            variant: "destructive",
            title: "Missing Required Fields",
            description: `Please fill in the following fields: ${missingFields.join(", ")}`,
          });
          return;
        }

        // Clean data: create PostureAnalysis object
        const cleanedData: PostureAnalysis = {
          overhead_squat_anterior: postureAnalysis.overhead_squat_anterior,
          overhead_squat_lateral: postureAnalysis.overhead_squat_lateral,
          single_leg_anterior: postureAnalysis.single_leg_anterior,
          pulling_pushing_lateral: postureAnalysis.pulling_pushing_lateral,
        };

        try {
          // Check if posture analysis data already exists
          const hasExistingData = existingPostureAnalysis?.data && existingPostureAnalysis.data.length > 0;

          if (hasExistingData) {
            // Update existing posture analysis
            await updatePostureAnalysis({
              consultation_id: consultationId,
              org_id: orgId,
              data: cleanedData,
            }).unwrap();
            toast({
              description: "Posture analysis updated successfully",
              variant: "success",
            });
          } else {
            // Create new posture analysis
            await createPostureAnalysis({
              consultation_id: consultationId,
              org_id: orgId,
              data: cleanedData,
            }).unwrap();
            toast({
              description: "Posture analysis saved successfully",
              variant: "success",
            });
          }
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
        } catch (error: unknown) {
          const typedError = error as ErrorType;
          toast({
            variant: "destructive",
            title: "Error saving posture analysis",
            description: typedError?.data?.message || "Failed to save posture analysis",
          });
          return;
        }
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
      return;
    }

    if (activeStep === 5) {
      // Step 5: Goals
      const values = form.getValues();
      const consultationId = values.id;
      const goals = values.goals;

      if (consultationId && goals && goals.length > 0) {
        // Validate mandatory fields for each goal
        let hasError = false;
        
        goals.forEach((goal, index) => {
          const missingFields: string[] = [];
          if (!goal.goal_text) missingFields.push("Goal Description");
          if (!goal.days_per_week) missingFields.push("Days Per Week");
          if (!goal.duration_per_day) missingFields.push("Time Per Day");

          if (missingFields.length > 0) {
            toast({
              variant: "destructive",
              title: `Missing Required Fields in Goal ${index + 1}`,
              description: `Please fill in: ${missingFields.join(", ")}`,
            });
            hasError = true;
          }
        });

        if (hasError) return;

        // Check for duplicate goals
        const goalTexts = goals.map((g) => g.goal_text.trim().toLowerCase());
        const uniqueGoals = new Set(goalTexts);
        if (uniqueGoals.size !== goalTexts.length) {
          toast({
            variant: "destructive",
            title: "Duplicate Goals",
            description: "Duplicate goals are not allowed. Please ensure each goal is unique.",
          });
          return;
        }

        try {
           const hasExistingData = existingGoal?.goals && existingGoal.goals.length > 0;

           if (hasExistingData) {
             await updateGoal({
               consultation_id: consultationId,
               org_id: orgId,
               data: goals,
             }).unwrap();
             toast({
               description: "Goals updated successfully",
               variant: "success",
             });
           } else {
             await createGoal({
               consultation_id: consultationId,
               org_id: orgId,
               data: goals,
             }).unwrap();
             toast({
               description: "Goals saved successfully",
               variant: "success",
             });
           }
           setActiveStep((prevActiveStep) => prevActiveStep + 1);
        } catch (error: unknown) {
          const typedError = error as ErrorType;
          toast({
            variant: "destructive",
            title: "Error saving goals",
            description: typedError?.data?.message || "Failed to save goals",
          });
          return;
        }
      } else {
        // If no goals, maybe allowed? Or warn? Assuming required.
         toast({
            variant: "destructive",
            title: "No Goals Added",
            description: "Please add at least one goal.",
          });
        return;
      }
      return;
    }

    if (activeStep === 6) {
        // Step 6: Meal Plan
        const values = form.getValues();
        const consultationId = values.id;
        const assignedMealPlanIds = values.assigned_meal_plan_ids;

        if (!consultationId) {
            toast({ variant: "destructive", title: "Consultation ID missing." });
            return;
        }

        try {
            if (assignedMealPlanIds && assignedMealPlanIds.length > 0) {
                // Mode: Assign Existing
                await assignMealPlan({
                    consultation_id: consultationId,
                    org_id: orgId,
                    data: { meal_plan_ids: assignedMealPlanIds },
                }).unwrap();
                
                 toast({
                    description: "Meal plans assigned successfully",
                    variant: "success",
                 });
            } else {
                 // Nothing selected
                 toast({
                    variant: "destructive",
                    title: "No Meal Plan Selected",
                    description: "Please select at least one meal plan.",
                 });
                 return;
            }

            setActiveStep((prev) => prev + 1);

        } catch (error: unknown) {
             const typedError = error as ErrorType;
              toast({
                variant: "destructive",
                title: "Error saving meal plan",
                description: typedError?.data?.message || "Failed to save meal plan",
              });
              return;
        }
        return;
    }
  };

  const handlePrev = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[1240px] p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-start gap-3">
                  <button type="button" onClick={handleClose}>
                    <i className="fa fa-xmark text-xl"></i>
                  </button>
                  <div>
                    <h1 className="font-medium">
                      {action == "add" ? "Create" : "Edit"} Consultation
                    </h1>
                    <CompactStepIndicator
                      activeStep={activeStep}
                      steps={steps}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {activeStep !== 1 && (
                    <Button
                      className="w-[80px]"
                      variant="outline"
                      onClick={handlePrev}
                      disabled={activeStep === 1}
                      type="button"
                    >
                      Previous
                    </Button>
                  )}
                  {activeStep !== steps.length && (
                    <LoadingButton
                      className="w-[80px]"
                      onClick={handleNext}
                      disabled={
                        activeStep === steps.length ||
                        isCreating ||
                        isSubmittingAnswers ||
                        isSavingMeasurement ||
                        isSavingBodyAnalysis ||
                        isUpdatingBodyAnalysis ||
                        isSavingPostureAnalysis ||
                        isUpdatingPostureAnalysis ||
                        isCreatingGoal ||
                        isUpdatingGoal ||
                        isAssigningMealPlan ||
                        isAssigningWorkoutPlan ||
                        isPatchingStatus ||
                        isCreatingMealPlan ||
                        (activeStep === 3 && isFetchingBodyAnalysis) ||
                        (activeStep === 4 && isFetchingPostureAnalysis) ||
                        (activeStep === 5 && isFetchingGoal)
                      }
                      loading={
                        isCreating ||
                        isSubmittingAnswers ||
                        isSavingMeasurement ||
                        isSavingBodyAnalysis ||
                        isUpdatingBodyAnalysis ||
                        isSavingPostureAnalysis ||
                        isUpdatingPostureAnalysis ||
                        isCreatingGoal ||
                        isUpdatingGoal ||
                        isAssigningMealPlan ||
                        isAssigningWorkoutPlan ||
                        isPatchingStatus ||
                        isCreatingMealPlan
                      }
                      type="button"
                    >
                      Next
                    </LoadingButton>
                  )}
                  {activeStep === steps.length && (
                    <LoadingButton
                      loading={isSubmitting || isAssigningWorkoutPlan || isPatchingStatus}
                      disabled={isSubmitting || isAssigningWorkoutPlan || isPatchingStatus}
                      type="submit"
                      className="w-[80px]"
                    >
                      {!isSubmitting && (
                        <i className="fa fa-floppy-disk pr-2"></i>
                      )}
                      {action == "add" ? "Save" : "Update"}
                    </LoadingButton>
                  )}
                </div>
              </div>
            </SheetHeader>

            <SheetDescription className="h-full p-6">
              <div className="flex gap-x-6">
                {/* Vertical Stepper - Sticky on the left */}
                {/* <div className="flex-shrink-0 w-48">
                  <CompactStepIndicator
                    activeStep={activeStep}
                    steps={steps}
                    orientation="vertical"
                  />
                </div> */}

                {/* Form Content - Scrollable on the right */}
                <div className="flex-1">
                  {getStepContent(activeStep, { action, open: isOpen, setAction, refetch, openSections, setOpenSections })}
                </div>
              </div>
            </SheetDescription>
          </form>
        </FormProvider>
        <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes. Are you sure you want to close? Your
                changes will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={forceClose} className="bg-destructive hover:bg-destructive/90">
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
};

export default ConsultationForm;
